import { AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../alert';
import { Body2Typography } from '@components/atoms';
import { Button, ButtonProps, ButtonSize, ButtonVariant } from '@components/molecules';
import type { CompositionProp } from '@utils';

type AlertDialogWarningLayoutProps = React.PropsWithChildren<{
	icon?: React.ReactNode;
	title: CompositionProp;
	primaryButton: CompositionProp;
	secondaryButton?: CompositionProp;
	tertiaryButton?: CompositionProp;
	backButton?: CompositionProp;
	onPrimaryAction: () => void;
	onSecondaryAction?: () => void;
	onTertiaryAction?: () => void;
	primaryButtonProps?: ButtonProps;
}>;

export const AlertDialogPrimaryLayout: React.FC<AlertDialogWarningLayoutProps> = ({
	icon,
	title,
	children,
	primaryButton,
	secondaryButton,
	tertiaryButton,
	onPrimaryAction,
	onSecondaryAction,
	onTertiaryAction,
	primaryButtonProps,
}) => {
	const titleComponent =
		typeof title === 'string' ? (
			<AlertDialogTitle className="text-primary">{title}</AlertDialogTitle>
		) : (
			(title as React.ReactNode)
		);

	const primaryButtonComponent =
		typeof primaryButton === 'string' ? (
			<Button {...primaryButtonProps} size={ButtonSize.SM} type="submit" onClick={onPrimaryAction} data-testid="alert-dialog-primary-button">
				{primaryButton}
			</Button>
		) : (
			(primaryButton as React.ReactNode)
		);

	const secondaryButtonComponent =
		typeof secondaryButton === 'string' ? (
			<Button variant={ButtonVariant.SECONDARY} size={ButtonSize.SM} onClick={onSecondaryAction} data-testid="alert-dialog-secondary-button">
				{secondaryButton}
			</Button>
		) : (
			(secondaryButton as React.ReactNode)
		);

	const secondaryBordarButtonComponent =
		typeof secondaryButton === 'string' ? (
			<Button variant={ButtonVariant.SECONDARY_BORDER} size={ButtonSize.SM} onClick={onSecondaryAction} data-testid="alert-dialog-secondary-button">
				{secondaryButton}
			</Button>
		) : (
			(secondaryButton as React.ReactNode)
		);

	const tertiaryButtonComponent =
		typeof tertiaryButton === 'string' ? (
			<Button variant={ButtonVariant.HYPERLINK} size={ButtonSize.SM} onClick={onTertiaryAction} data-testid="alert-dialog-tertiary-button">
				{tertiaryButton}
			</Button>
		) : (
			(tertiaryButton as React.ReactNode)
		);

	return (
		<AlertDialogContent className="max-h-[90vh] w-[520px] gap-2 sm:max-w-[95vw]">
			<AlertDialogHeader className="grow-0 justify-items-center">
				<div className="self-center">{icon && icon}</div>
				{titleComponent}
			</AlertDialogHeader>
			<div className="shrink overflow-y-auto wrap-break-word whitespace-pre-line px-4 pt-4 text-center">
				<Body2Typography className="text-secondary" truncate={false}>
					{children}
				</Body2Typography>
			</div>
			<AlertDialogFooter className="grow-0 flex-col items-center space-y-2">
				<div className="flex flex-row space-x-4">
					{Boolean(secondaryButtonComponent) && !tertiaryButtonComponent && secondaryButtonComponent}
					{Boolean(secondaryButtonComponent) && Boolean(tertiaryButtonComponent) && secondaryBordarButtonComponent}
					{primaryButtonComponent}
				</div>
				{Boolean(tertiaryButtonComponent) && tertiaryButtonComponent}
			</AlertDialogFooter>
		</AlertDialogContent>
	);
};
